/**
 * ConvexErrorBoundary — React error boundary for Convex query/mutation errors.
 *
 * Catches errors thrown through React's error-propagation path. Convex's useQuery
 * rethrows ConvexError values during the React render commit phase when a query
 * handler throws; this boundary captures them.
 *
 * Renders the fallback when an error is caught; reset by re-mounting the boundary
 * (which the route-level wrapping accomplishes when the URL changes).
 *
 * @see specs/seed-source.md Section 8.12
 */
import React from "react";

interface ConvexErrorBoundaryProps {
  fallback: React.ReactNode;
  children: React.ReactNode;
}

interface ConvexErrorBoundaryState {
  hasError: boolean;
}

export default class ConvexErrorBoundary extends React.Component<
  ConvexErrorBoundaryProps,
  ConvexErrorBoundaryState
> {
  constructor(props: ConvexErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(_error: Error): ConvexErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    // Log the error for observability. Reserve console.error for
    // non-ConvexError exceptions (platform errors).
    try {
      console.error(
        JSON.stringify({
          event: "convex_error_boundary_caught",
          error: error.message,
          componentStack: errorInfo.componentStack,
        }),
      );
    } catch {
      // Logging failure must not crash the boundary.
    }
  }

  render(): React.ReactNode {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}
